import { useState } from 'react'

import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { useAktion, useLaden } from '../hooks'
import type { Artikel, KladdePlatz } from '../typen'
import ArtikelAuswahl from './ArtikelAuswahl'
import Meldungen from './Meldungen'

/**
 * Der Platz eines Artikels in der Kladde (02.11.2026): wo er steht, und „einordnen nach Artikel …". Anlass:
 * 10535 Pflanzenfett Stange landete als eigener Block am Blattende; „trag sie beim Stangenwickler weiß ein" ging
 * nicht. Mit „gemeinsame Spalte" kommen zwei Fertigartikel in einen Block mit einer Produktionsspalte, wie
 * „10578 + 10535" in Frau Barths Excel. Ändert nur die Anzeige von Kladde und Exporten, nichts an der Rechnung.
 */

/** „Blatt Stange: Block „…“, Spalte 10578 + 10535, Platz 3 von 7, nach … / vor …" */
function platzText(p: KladdePlatz): string {
  const nachbarn = [p.davor && `nach „${p.davor}“`, p.danach && `vor „${p.danach}“`].filter(Boolean).join(', ')
  return (
    `Blatt ${p.blatt}: Block „${p.block}“` +
    (p.spalte ? `, Spalte ${p.spalte}` : '') +
    `, Platz ${p.position} von ${p.von}` +
    (nachbarn ? ` (${nachbarn})` : '')
  )
}

export default function PlatzInDerKladde({ artikel, alleArtikel, onGeaendert }: { artikel: Artikel; alleArtikel: Artikel[]; onGeaendert: () => void }) {
  const nummer = artikel.artikelnummer
  const { daten, fehler: ladeFehler, setDaten } = useLaden(() => ohne404(api.kladdePlatz(nummer)), [nummer])
  const { fehler, erfolg, arbeitet, setFehler, setErfolg, ausfuehren } = useAktion()
  const [nach, setNach] = useState('')
  const [gemeinsam, setGemeinsam] = useState(false)

  // Zur Wahl stehen zuerst die Artikel desselben Blatts - dort will man ihn hin.
  const andere = alleArtikel.filter((a) => a.artikelnummer !== nummer && a.aktiv)
  const imBlatt = artikel.gebindegruppe ? andere.filter((a) => a.gebindegruppe === artikel.gebindegruppe) : []
  const auswahl = imBlatt.length > 0 ? imBlatt : andere
  const nachArtikel = alleArtikel.find((a) => a.artikelnummer === nach)
  // Eine gemeinsame Produktionsspalte gibt es nur für zwei Fertigartikel.
  const gemeinsamMoeglich = artikel.art === 'fertigartikel' && (!nachArtikel || nachArtikel.art === 'fertigartikel')

  if (daten === NICHT_VORHANDEN) return <p className="hinweis">Den Platz in der Kladde liefert das Backend noch nicht.</p>
  const plaetze = daten ?? []

  function einordnen() {
    if (!nach) {
      setFehler('Den Artikel wählen, hinter den er kommt.')
      return
    }
    void ausfuehren(async () => {
      const antwort = await api.einordnen(nummer, nach, gemeinsam && gemeinsamMoeglich)
      if (antwort.platz) setDaten(antwort.platz)
      const wo = (antwort.platz ?? []).map(platzText).join('; ')
      setErfolg(
        `${nummer} steht jetzt ${gemeinsam && gemeinsamMoeglich ? 'im Block von' : 'hinter'} ${nach}${wo ? ` - ${wo}` : ''}.` +
          (antwort.verschoben ? ` ${antwort.verschoben} Artikel rückten dafür eine Spalte nach hinten.` : '') +
          ' Zurück geht es mit erneutem Einordnen.',
      )
      setNach('')
      setGemeinsam(false)
      onGeaendert()
    })
  }

  return (
    <div>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      <p className="hinweis" style={{ marginTop: 0 }}>
        Spalte in der Kladde: {artikel.reihenfolge ?? 'keine eigene (am Blattende)'}.
        {daten && plaetze.length === 0 && ' Der Artikel steht in keinem Blatt - ohne Blatt oder ohne Kopplung.'}
      </p>
      {plaetze.length > 0 && (
        <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
          {plaetze.map((p) => (
            <li key={`${p.blatt}-${p.block}`}>{platzText(p)}</li>
          ))}
        </ul>
      )}
      <div className="leiste felder" style={{ marginBottom: 0 }}>
        <label className="feld">
          <span className="hinweis">Einordnen nach Artikel</span>
          <ArtikelAuswahl artikel={auswahl} wert={nach} onChange={setNach} leer="bitte wählen" />
        </label>
        {artikel.art === 'fertigartikel' && (
          <label
            className="hinweis"
            title="Beide Fertigartikel in einen Block mit einer Produktionsspalte (Sammelzelle wie „10578 + 10535“) - nur für zwei Fertigartikel"
          >
            <input type="checkbox" checked={gemeinsam && gemeinsamMoeglich} disabled={!gemeinsamMoeglich} onChange={(e) => setGemeinsam(e.target.checked)} />{' '}
            gemeinsame Spalte
          </label>
        )}
        <button disabled={arbeitet || !nach} onClick={einordnen}>
          Einordnen
        </button>
      </div>
    </div>
  )
}
